import { GetServerSideProps } from 'next';
import pMap from 'p-map';
import { capitalize, pick, sortBy } from 'lodash-es';
import Head from 'next/head';
import {
  ConfigT,
  PageType,
  SellDevicePageT,
  StreamlinedDeviceT,
} from '@/lib/buyback/types';
import {
  convertListingToDevice,
  findObjectByHandle,
  generatePageType,
  generateSteps,
} from '@/lib/buyback/helpers';
import Layout from '@/components/buyback/v2/Layout';
import settings from '@/settings';
import configs from '@/settings/configs/buyback/us';
import {
  getActiveBrands,
  getActivePskusForBrand,
} from '@/lib/backend/buybackListings';
import findPimProduct from '@/lib/backend/findPimProduct';
import { getEdgeUrl } from '@/lib/getEdgeUrl';
import { BuybackUSConfig } from '@/settings/configs/buyback/us/type';
import SelectDevice from '@/components/buyback/v2/sell/SelectDevice';
import Schema from '@/lib/buyback/Seo';
import { REDIRECT_HOME } from '@/lib/common';

export const getServerSideProps: GetServerSideProps<SellDevicePageT> = async (
  event,
) => {
  if (!['reebelo-dev', 'reebelo-us'].includes(settings.store))
    return REDIRECT_HOME;

  const { query } = event;
  const handle = query.handle as string;

  let config = findObjectByHandle(configs, handle);

  if (config != null) {
    const pageType = generatePageType(config.step);
    const activeBrands: string[] = await getActiveBrands(handle);
    const currentContent = config?.device?.content as BuybackUSConfig[];
    const activeContent = currentContent?.filter((c: any) =>
      activeBrands.includes(c.title),
    );

    config = {
      ...config,
      device: {
        ...config?.device,
        content:
          pageType === PageType.NAVIGATION ? currentContent : activeContent,
      },
    } as ConfigT;

    const listings =
      pageType === PageType.MODEL &&
      config.device.category &&
      config.device.title
        ? await getActivePskusForBrand(
            config.device.title,
            config.device.category,
          )
        : [];

    // ingest release date from PIM for sorting
    const withPIMListing = await pMap(listings, async (list) => {
      const psku = list.pimPSKU;
      const pim = await findPimProduct(psku);

      return {
        ...list,
        pim,
        releaseDate: pim?.releaseDate || '1990-01-01',
      };
    });

    const streamlinedListings = sortBy(
      withPIMListing.map((item) =>
        pick(item, [
          'image',
          'model',
          'slug',
          'condition',
          'brand',
          'category',
          'releaseDate',
        ]),
      ),
      ['releaseDate', 'slug'],
    ).reverse();

    const devices = convertListingToDevice(streamlinedListings);

    const streamlineContent = async () => {
      if (pageType === PageType.MODEL) return devices;

      if (pageType === PageType.NAVIGATION) {
        const content = (config?.device?.content as BuybackUSConfig[]) ?? [];

        const filteredContent = await pMap(content, async (item) => {
          const activeSubBrands = await getActiveBrands(item?.handle ?? '');

          return activeSubBrands.length > 0
            ? {
                text: item.title ?? '',
                link: item.handle ?? '',
                image: getEdgeUrl(
                  `images/buyback/categories/${item.handle}.png`,
                ),
              }
            : null;
        });

        return filteredContent.filter(Boolean);
      }

      // Brands
      return (
        (config?.device?.content as BuybackUSConfig[])?.map((item) => ({
          text: item.title ?? '',
          link: item.handle ?? '',
          image: getEdgeUrl(
            `images/buyback/brands/${item?.title?.toLowerCase()}.svg`,
          ),
        })) ?? []
      );
    };

    const content = (await streamlineContent()) as StreamlinedDeviceT[];

    return {
      props: {
        device: { ...config.device, content },
        step: config.step,
        pageType,
      },
    };
  }

  return REDIRECT_HOME;
};

// Handle Category + Brands + Models Selection
const SellDevicePage = (props: SellDevicePageT) => {
  const steps = generateSteps();

  // Generate Page Title
  const pageTitle = (() => {
    switch (props.pageType) {
      case PageType.NAVIGATION:
        return `Sell Your Electronics on Reebelo`;

      case PageType.BRAND:
        return `Sell & Upgrade Your ${props.device.title}`;
      case PageType.MODEL:
        return `Sell & Upgrade Your ${props.device.title} ${capitalize(
          props.device.category,
        )}`;

      default:
        return `Sell Your Electronics on Reebelo`;
    }
  })();

  return (
    <>
      <Head>
        <title>{pageTitle} - Quick & Easy BuyBack Process</title>
        {/* Disable auto zoom in on input focus  */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no"
        />
        <Schema />
      </Head>
      <div className="text-sm text-gray-700">
        <Layout
          device={props.device}
          pageType={props.pageType}
          step={props.step}
          steps={steps}
        >
          <SelectDevice
            device={props.device}
            pageType={props.pageType}
            progress={{
              step: props.step,
              steps,
            }}
          />
        </Layout>
      </div>
    </>
  );
};

export default SellDevicePage;
